import { useContext, useRef } from "react";
import { Empty, Modal, Space, Table as TableAntd, Tag, notification } from "antd";
import { ClockCircleOutlined, CopyOutlined, LinkOutlined, SyncOutlined } from "@ant-design/icons";
import { SortOrder } from "antd/es/table/interface";
import TableActionButton from "../../../../components/TableActionButton";
import { useGetColumnSearchPropsData } from "../../../../hooks/FilterTable/date";
import { useGetColumnSearchProps } from "../../../../hooks/FilterTable/stringClearFiltros";
import { IInvoice } from "../../../../interfaces";
import { formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import { getLinkInvoice, normalizeLink, verifyLinkIsValid } from "../../../../utils/Functions";
import StatusTag from "../StatusTag";
import { ThemeContext } from "../../../../contexts/ThemeContext";

interface ITableData extends IInvoice {
    key: number;
}
interface IProps {
    open: boolean;
    isLoading: boolean;
    onClose: () => void;
    invoices: IInvoice[];
    onReprocessInvoice: (invoiceId: number) => void;
    loadingReprocess: boolean;
}

export default function ModalInvoicesInProcessing({
    open,
    onClose,
    isLoading,
    invoices,
    onReprocessInvoice,
    loadingReprocess,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const refFuncaoLimparFiltrosString = useRef<any>(null);

    const columns = [
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            width: '8%',
            sorter: (a: ITableData, b: ITableData) => a?.type?.localeCompare(b?.type || '')!,
            showSorterTooltip: false,
            ...useGetColumnSearchProps({ dataIndex: 'type', ref: refFuncaoLimparFiltrosString }),
            render: (type: string) => <Tag color="blue" style={{ margin: 0, fontWeight: 600, borderRadius: 4 }}>{type}</Tag>,
        },
        {
            title: 'Número',
            dataIndex: 'number',
            key: 'number',
            showSorterTooltip: false,
            ...useGetColumnSearchProps({ dataIndex: 'number', ref: refFuncaoLimparFiltrosString }),
            render: (num: string) => <span style={{ fontWeight: 500 }}>{num || '--'}</span>,
        },
        {
            title: 'Código Autenticidade',
            dataIndex: 'autenticity_code',
            key: 'autenticity_code',
            ellipsis: true,
            showSorterTooltip: false,
            ...useGetColumnSearchProps({ dataIndex: 'autenticity_code', ref: refFuncaoLimparFiltrosString }),
            render: (code: string) => <span style={{ fontFamily: 'monospace' }}>{code || '--'}</span>,
        },
        {
            title: 'Data Importação',
            dataIndex: 'created_at',
            key: 'created_at',
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: ITableData, b: ITableData) => new Date(a?.created_at!).getTime() - new Date(b?.created_at!).getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({ dataIndex: 'created_at' }),
            render: (data: any) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data) : '--'}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a: ITableData, b: ITableData) => a.status.localeCompare(b.status),
            showSorterTooltip: false,
            filters: [
                { text: 'Concluído', value: 'Concluído' },
                { text: 'Com erro', value: 'Erro' },
                { text: 'Alerta', value: 'Alerta' },
            ],
            onFilter: (value: any, record: ITableData) => record.status === value,
            render: (_: any, { id, status, message }: ITableData) => (
                <StatusTag
                    status={status}
                    message={message}
                    key={`tag-${id}`}
                />
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: '12%',
            render: (_: any, record: ITableData) => {
                const hasLink = Boolean(record.link && verifyLinkIsValid({ link: record.link }));
                const hasKey = Boolean(record.key);

                return (
                    <Space size={4}>
                        {hasLink && (
                            <TableActionButton
                                action="custom"
                                icon={<LinkOutlined />}
                                tooltip="Abrir link da nota"
                                onClick={() => window.open(normalizeLink(record.link!), '_blank')}
                            />
                        )}
                        {hasKey && (
                            <TableActionButton
                                action="custom"
                                icon={<CopyOutlined />}
                                tooltip="Copiar chave de acesso"
                                onClick={() => {
                                    navigator.clipboard.writeText(String(record.key));
                                    notification.success({
                                        message: "Chave copiada!",
                                        description: (
                                            <a href={getLinkInvoice(record.type || 'NFE')} target="_blank" rel="noopener noreferrer">
                                                Abrir link SEFAZ
                                            </a>
                                        ),
                                        duration: 3,
                                    });
                                }}
                            />
                        )}
                        <TableActionButton
                            action="custom"
                            icon={<SyncOutlined spin={loadingReprocess} />}
                            tooltip="Reprocessar nota"
                            onClick={() => onReprocessInvoice(record.id)}
                            style={{ color: '#fa8c16' }}
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <Modal
            width={1100}
            open={open}
            title={
                <Space align="center">
                    <ClockCircleOutlined style={{ color: '#fa8c16' }} />
                    <span style={{ fontWeight: 600 }}>Notas em Processamento</span>
                </Space>
            }
            footer={null}
            onCancel={onClose}
            centered
            bodyStyle={{ padding: '8px 0 0 0' }}
        >
            <TableAntd
                loading={isLoading}
                columns={columns}
                dataSource={invoices.map((inv) => ({
                    ...inv,
                    key: inv.id,
                }))}
                className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                rowKey="id"
                scroll={{ x: 950 }}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Nenhuma nota fiscal em processamento encontrada."
                        />
                    ),
                }}
                pagination={{
                    pageSize: 8,
                    showSizeChanger: true,
                    pageSizeOptions: ['8', '15', '30'],
                    size: 'small',
                }}
            />
        </Modal>
    );
}

